import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ErrorCode } from '@bliks-io/contracts';
import type { DeletedCanvasNode } from '@bliks-io/ui';
import { toastApiError } from '@/api/mutations/toast-api-error';
import type { UndoableEditorActions } from './useUndoableEditorActions';

/**
 * Partition a canvas deletion batch by the builder's `canvasKind` stamp
 * (carried on each descriptor — no more re-deriving node kinds from sibling
 * collections or id prefixes):
 *  - `step` / `decision` → `deleteSteps` (batched)
 *  - `assignment` → `deleteAssignment` per id
 *  - `event-start` / `event-intermediate` → `deleteEvent` (raw-id event
 *    nodes; `eventId` when stamped on the node data, else the node id IS the
 *    event id)
 *  - `event-legacy` → upgraded to a first-class event first (server-side),
 *    then deleted
 *  - `end` / `swimlane` / `synthetic-gateway` → no-op (derived nodes with no
 *    backing entity; the editor filters them out before this fires anyway)
 */
export function useDraftNodeDeletion(
  undoableActions: UndoableEditorActions
): (nodes: DeletedCanvasNode[]) => void {
  const { t } = useTranslation();
  return useCallback(
    (nodes: DeletedCanvasNode[]) => {
      const stepIds: string[] = [];
      const work: Promise<unknown>[] = [];
      for (const { nodeId, kind, eventId } of nodes) {
        switch (kind) {
          case 'step':
          case 'decision':
            stepIds.push(nodeId);
            break;
          case 'assignment':
            work.push(undoableActions.deleteAssignment(nodeId));
            break;
          case 'event-start':
          case 'event-intermediate':
            work.push(undoableActions.deleteEvent(eventId ?? nodeId));
            break;
          case 'event-legacy':
            work.push(undoableActions.deleteLegacyEvent(nodeId));
            break;
          case 'end':
          case 'swimlane':
          case 'synthetic-gateway':
          case 'note': // Deleted via their own handler, not the node-delete batch.
          case 'empty-step':
            break;
          default:
            kind satisfies never;
        }
      }
      if (stepIds.length > 0) work.push(undoableActions.deleteSteps(stepIds));
      // The cache already self-heals (optimistic strip → onError restore →
      // settle refetch); this catch only owns user feedback. Without it a
      // rejected delete (e.g. the backend's ≥1-start invariant) lands as an
      // uncaught rejection with no toast — the node silently flickers back.
      void Promise.all(work).catch((error: unknown) => {
        if (isStartRequired(error)) {
          toast.error(t('errors.processEvent.startRequired'));
          return;
        }
        toastApiError(error, 'errors.processDraft.deleteFailed');
        console.error(error);
      });
    },
    [undoableActions, t]
  );
}

// The backend ApiError's `message` is the i18n error-code string, so the same
// value both classifies the rejection and feeds the toast. Mirrors the
// soft-error recogniser shape in api/mutations/helpers/draftMutation.ts.
function isStartRequired(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { statusCode?: unknown; message?: unknown };
  return e.statusCode === 400 && e.message === ErrorCode.PROCESS_EVENT_START_REQUIRED;
}
